// app-ui/src/components/settings/GitSettings.tsx
import React, { useState, useEffect } from 'react';
import Modal from '../../../../../shared/ui/Modal';
import { GitHubService } from '../../../tabs/code';
import {
    getProjectGitSettingsFromStorage,
    saveProjectGitSettingsToStorage,
} from '../../../../../shared/lib/storageUtils';
import { getProject, updateProjectGitSettings } from '../../api/projectsApi';
import type { ProjectGitSettings } from '../../types';

interface GitSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    /** When set, load/save git settings at project level (DynamoDB + localStorage keyed by project). */
    projectId?: string | number | null;
    onSettingsUpdated?: () => void;
}

interface GitHubSettings {
    repo_url: string;
    branch: string;
    token?: string;
}

const DEFAULT_SETTINGS: GitHubSettings = {
    repo_url: '',
    branch: 'main',
    token: ''
};

function toGitHubSettings(g: ProjectGitSettings | null | undefined): GitHubSettings {
    if (!g || typeof g !== 'object') return { ...DEFAULT_SETTINGS };
    const o = g as Record<string, unknown>;
    const repoUrl = String(g.repo_url ?? o.repository_path ?? '').trim();
    const branch = String(g.branch ?? o.branch_or_feature ?? 'main').trim() || 'main';
    const token = String(g.token ?? o.github_token ?? '');
    return {
        repo_url: repoUrl,
        branch,
        token,
    };
}

const GitSettings: React.FC<GitSettingsProps> = ({ isOpen, onClose, projectId = null, onSettingsUpdated }) => {
    const [settings, setSettings] = useState<GitHubSettings>(DEFAULT_SETTINGS);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const load = async () => {
            setIsLoading(true);
            setSettings(DEFAULT_SETTINGS);
            try {
                if (projectId != null) {
                    const projectScoped = getProjectGitSettingsFromStorage(projectId, {
                        includeGlobalFallback: false,
                    });
                    if (projectScoped && String(projectScoped.repo_url ?? '').trim()) {
                        setSettings({
                            repo_url: projectScoped.repo_url ?? '',
                            branch: projectScoped.branch ?? 'main',
                            token: projectScoped.token ?? '',
                        });
                        return;
                    }
                    try {
                        const project = await getProject(projectId);
                        if (project.git_settings) {
                            const s = toGitHubSettings(project.git_settings);
                            if (String(s.repo_url).trim()) {
                                setSettings(s);
                                saveProjectGitSettingsToStorage(projectId, s);
                                return;
                            }
                        }
                    } catch (e) {
                        console.debug('Failed to load project git settings from API, falling back to localStorage:', e);
                    }
                }
                const stored = getProjectGitSettingsFromStorage(projectId);
                if (stored) {
                    setSettings({
                        repo_url: stored.repo_url ?? '',
                        branch: stored.branch ?? 'main',
                        token: stored.token ?? '',
                    });
                }
            } finally {
                setIsLoading(false);
            }
        };

        load();
    }, [isOpen, projectId]);

    const updateField = (field: keyof GitHubSettings, value: string) => {
        setSettings(prev => ({ ...prev, [field]: value }));
        if (connectionStatus) setConnectionStatus(null);
    };

    const testGitHubConnection = async () => {
        if (!settings.repo_url) {
            setConnectionStatus('Please enter a repository path');
            return;
        }
        setIsTestingConnection(true);
        setConnectionStatus(null);
        try {
            saveProjectGitSettingsToStorage(projectId, {
                repo_url: settings.repo_url,
                branch: settings.branch,
                token: settings.token
            });
            const repoInfo = GitHubService.parseRepoPath(settings.repo_url);
            if (!repoInfo) throw new Error('Invalid repository format. Please use: owner/repository');
            await GitHubService.getRepository(repoInfo.owner, repoInfo.repo, settings.token);
            setConnectionStatus('✅ Connection successful!');
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Connection failed';
            setConnectionStatus(`❌ ${msg}`);
        } finally {
            setIsTestingConnection(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                repo_url: settings.repo_url,
                branch: settings.branch,
                token: settings.token
            };
            saveProjectGitSettingsToStorage(projectId, payload);

            if (projectId != null) {
                try {
                    await updateProjectGitSettings(projectId, payload);
                } catch (e) {
                    console.error('Failed to persist project git settings to API:', e);
                }
            }

            onSettingsUpdated?.();
            try {
                window.dispatchEvent(new CustomEvent('vishmaker-git-settings-saved'));
            } catch {
                /* ignore */
            }
            onClose();
        } catch (error) {
            console.error('Failed to save GitHub settings:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Git Settings" size="md" panelSurface="solid">
            <div className="space-y-6">
                {projectId != null ? (
                    <p className="text-xs text-slate-400">
                        Stored per project. These settings apply only to the current project.
                    </p>
                ) : (
                    <p className="text-xs text-slate-400">
                        No project selected yet — saved as defaults in this browser. Discovery Proceed uses them right away;
                        after your project is created, open Git settings again from the project list to sync to the server if
                        needed.
                    </p>
                )}

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-white/90">Repository Path</label>
                    <input
                        type="text"
                        value={settings.repo_url}
                        onChange={e => updateField('repo_url', e.target.value)}
                        placeholder="owner/repository"
                        className="w-full px-4 py-3 bg-slate-950/80 border border-white/25 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-400/50 transition-all duration-300"
                    />
                    <p className="text-xs text-slate-400">Enter your GitHub repository in the format: owner/repository</p>
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-white/90">Branch</label>
                    <input
                        type="text"
                        value={settings.branch}
                        onChange={e => updateField('branch', e.target.value)}
                        placeholder="main"
                        className="w-full px-4 py-3 bg-slate-950/80 border border-white/25 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-400/50 transition-all duration-300"
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-white/90">GitHub Token</label>
                    <input
                        type="password"
                        value={settings.token}
                        onChange={e => updateField('token', e.target.value)}
                        placeholder="ghp_..."
                        className="w-full px-4 py-3 bg-slate-950/80 border border-white/25 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-400/50 transition-all duration-300"
                    />
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={testGitHubConnection}
                            disabled={isTestingConnection || !settings.repo_url}
                            className={`
                                px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300
                                ${isTestingConnection || !settings.repo_url
                                    ? 'bg-white/10 text-gray-400 cursor-not-allowed'
                                    : 'bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-400/30'
                                }
                            `}
                        >
                            {isTestingConnection ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Testing...
                                </>
                            ) : (
                                'Test Connection'
                            )}
                        </button>
                        {connectionStatus && <span className="text-sm text-gray-300">{connectionStatus}</span>}
                    </div>
                    <p className="text-xs text-gray-400">
                        Optional: Add a GitHub Personal Access Token for higher rate limits and private repository access.
                    </p>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-700/70">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-green-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Loading...' : isSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default GitSettings;
